import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  collection,
  collectionGroup,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";

import Sidebar from "../../DesignSystem/Sidebar";
import {
  auth,
  dbAML,
  dbFinancialPromotions,
} from "../../../backend/firebaseconfig";

import {
  getPortalGreetingDataFromTimestamp,
  resolveFirstNameFromUser,
} from "../../PortalSections/utils/portalGreetingHelpers";

import { useUserEmail } from "../../../context/UserEmailContext";
import { useUserRole } from "../../../context/UserRoleContext";
import { useAdviserStatus } from "../../../context/AdviserStatusContext";
import { useAdminStatus } from "../../../context/AdminStatusContext";
import { getPortalSectionConfig } from "../../PortalSections/utils/portalSectionConfig";
import { useUserPermissions } from "../../../context/UserPermissionsContext";
import { useAdminAdviserMails } from "../../../context/AdminAdviserEmailsContext";
import { useServerSyncedTime } from "../../../context/utils/useServerSyncedTime";
import { getRelatedEmails } from "../AML/Utils/getRelatedEmails";

import {
  subscribeAnnouncements,
  subscribeUserAnnouncementAcknowledgements,
} from "./utils/announcementService";

import { getVisibleAnnouncementsForUser } from "./utils/announcementDisplayUtils";

import AnnouncementPopup from "./components/AnnouncementPopup";
import StatusTag from "../FinancialPromotions/components/buttons/StatusTag";
import StatusButton from "../AML/Components/Buttons/StatusButton";

import "../../../css/Announcements/MyDashboard.css";
import dayjs from "dayjs";
import AnnouncementCard from "./components/AnnouncementCard";
import RatesMonitor from "./components/RatesMonitor";
import { FORCE_BIRTHDAY_GREETING, isBirthday, loadBirthday } from "./utils/birthdayGreeting";
import Tabs from "../../DesignSystem/Tabs/Tabs";
import capitalizeFirst from "../AML/Checks/utils/factories/capitalise";
import { formatCheckType } from "../AML/CheckDetails/Utils/formatCheckType";
import { computeOverallOutcome } from "../AML/CheckDetails/Utils/outcome/checkOutcome";
import { getAllMatchesResolved } from "../AML/CheckDetails/Utils/Experian/allMatchesHaveReasons";
import { colorForResult } from "../AML/CheckDetails/Utils/getColorForDecision";
import { ReactComponent as LatestAnnouncetmentIcon } from "../../../assets/icons/dashboard/LatestAnnouncements.svg";
import { ReactComponent as Notifications } from "../../../assets/icons/dashboard/Notifications.svg";
import { ReactComponent as PastAnnouncementIcon } from "../../../assets/icons/dashboard/PastAnnouncements.svg";
import { ReactComponent as CalenderIcon } from "../../../assets/icons/dashboard/Date.svg";
import { ReactComponent as MyProfile } from "../../../assets/icons/dashboard/MyProfile.svg";

const getAnnouncementTimestamp = (announcement) => {
  if (announcement?.postedAtMs) return announcement.postedAtMs;
  if (announcement?.createdAtMs) return announcement.createdAtMs;

  const postedDate = announcement?.postedDate || announcement?.scheduleDate;

  if (postedDate) {
    const parsed = new Date(postedDate).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  const createdAtDate = announcement?.createdAt?.toDate?.();

  if (createdAtDate) {
    return createdAtDate.getTime();
  }

  return 0;
};

// const getFirebaseAccountCreatedAtMs = (currentUser) => {
//   const creationTime = currentUser?.metadata?.creationTime;

//   if (!creationTime) return 0;

//   const timestamp = new Date(creationTime).getTime();

//   return Number.isNaN(timestamp) ? 0 : timestamp;
// };

const getAnnouncementCreatedOrPostedMs = (announcement) => {
  if (announcement?.postedAtMs) return announcement.postedAtMs;
  if (announcement?.createdAtMs) return announcement.createdAtMs;

  if (announcement?.postedDate) {
    const parsed = dayjs(announcement.postedDate);
    return parsed.isValid() ? parsed.valueOf() : 0;
  }

  if (announcement?.scheduleDate) {
    const parsed = dayjs(announcement.scheduleDate);
    return parsed.isValid() ? parsed.valueOf() : 0;
  }

  const firestoreDate = announcement?.createdAt?.toDate?.();

  if (firestoreDate) {
    return firestoreDate.getTime();
  }

  return 0;
};

const getFirebaseAccountCreatedAtMs = (currentUser) => {
  const creationTime = currentUser?.metadata?.creationTime;

  if (!creationTime) return 0;

  const parsed = dayjs(creationTime);

  return parsed.isValid() ? parsed.valueOf() : 0;
};

const getFinancialPromotionStatusMeta = (status) => {
  const normalized = String(status || "submitted")
    .trim()
    .toLowerCase()
    .replaceAll("-", "_");

  // console.log("Determining status meta for:", { status, normalized });

  if (["approved", "completed"].includes(normalized)) {
    return {
      label: "Approved",
      className: "dashboard-notification-pill--approved",
    };
  }

  if (["declined", "rejected", "failed"].includes(normalized)) {
    return {
      label: "Declined",
      className: "dashboard-notification-pill--declined",
    };
  }

  if (
    [
      "action_required",
      "requested_changes",
      "changes_requested",
      "needs_action",
      "referred",
    ].includes(normalized)
  ) {
    return {
      label: "Action Required",
      className: "dashboard-notification-pill--action-required",
    };
  }

  return {
    label: "Submitted",
    className: "dashboard-notification-pill--submitted",
  };
};

const getTimestampMs = (value) => {
  if (!value) return 0;

  if (typeof value?.toDate === "function") {
    const date = value.toDate();
    return Number.isNaN(date?.getTime?.()) ? 0 : date.getTime();
  }

  if (typeof value?.seconds === "number") {
    return value.seconds * 1000;
  }

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getAMLStatusMeta = (status) => {
  const normalized = String(status || "processing")
    .trim()
    .toLowerCase()
    .replaceAll("-", "_")
    .replaceAll(" ", "_");

  if (["approved", "completed", "passed"].includes(normalized)) {
    return {
      label: "Completed",
      className: "dashboard-notification-pill--approved",
    };
  }

  if (["declined", "failed", "rejected"].includes(normalized)) {
    return {
      label: "Declined",
      className: "dashboard-notification-pill--declined",
    };
  }

  if (["referred", "action_required", "resubmission"].includes(normalized)) {
    return {
      label: "Action Required",
      className: "dashboard-notification-pill--action-required",
    };
  }

  return {
    label: "Processing",
    className: "dashboard-notification-pill--submitted",
  };
};

const formatAmlExpiration = (createdAt) => {
  const createdAtMs = getTimestampMs(createdAt);

  if (!createdAtMs) return "";

  const expiration = dayjs(createdAtMs).add(85, "day");

  return expiration.isValid() ? expiration.format("DD MMM YYYY") : "";
};

// const getFirebaseAccountCreatedAtMs = (currentUser) => {
//   const creationTime = currentUser?.metadata?.creationTime;

//   if (!creationTime) return 0;

//   const parsed = dayjs(creationTime);

//   return parsed.isValid() ? parsed.valueOf() : 0;
// };

const canViewAllChecks = (role, perms = []) =>
  role === "Staff" &&
  Array.isArray(perms) &&
  perms.includes("Check \u00b2 Admin");

const classifyBucket = (checkType) => {
  const ct = String(checkType || "")
    .trim()
    .toLowerCase();
  if (ct === "enhanced" || ct === "idv" || ct === "identity verification")
    return "idv";
  if (ct === "standard" || ct === "document only" || ct === "document")
    return "doconly";
  if (ct === "simple") return "dbWs";
  return "idv";
};

const amlColorToCardClass = (color) => {
  const map = {
    green: "dashboard-notification-pill--approved",
    red: "dashboard-notification-pill--declined",
    yellow: "dashboard-notification-pill--action-required",
    crimson: "dashboard-notification-pill--action-required",
    orange: "dashboard-notification-pill--processing",
    purple: "dashboard-notification-pill--resubmission",
  };
  return map[color] || "dashboard-notification-pill--submitted";
};

const getAMLClientName = (check) => {
  const firstName = check?.decision?.verification?.person?.firstName || "";
  const lastName = check?.decision?.verification?.person?.lastName || "";

  return check?.name || `${firstName} ${lastName}`.trim() || "Unnamed Client";
};

const MyDashboard = () => {
  const navigate = useNavigate();
  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { userPermissions = [] } = useUserPermissions();
  const { adviserStatus: loaAdviserStatus = [] } = useAdviserStatus();
  const { adminStatus: loaAdminStatus = [] } = useAdminStatus();
  const canSeeLoaTracker = getPortalSectionConfig({
    userRole, userPermissions, adviserStatus: loaAdviserStatus, adminStatus: loaAdminStatus,
  })["loa-toolkit"].show;
  const { adminAdviserMails } = useAdminAdviserMails();
  const [announcements, setAnnouncements] = useState([]);
  const [acknowledgedIds, setAcknowledgedIds] = useState([]);
  const [financialPromotions, setFinancialPromotions] = useState([]);
  const [amlChecks, setAmlChecks] = useState([]);
  const [amlChecksEnriched, setAmlChecksEnriched] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [birthday, setBirthday] = useState(null);
  const [notificationFilter, setNotificationFilter] = useState("All");
  useEffect(() => {
    if (!canSeeLoaTracker && notificationFilter === "LOA Tracker") {
      setNotificationFilter("All");
    }
  }, [canSeeLoaTracker, notificationFilter]);
  const server =
    process.env.REACT_APP_STAFF_SERVER_URL ||
    "https://rosemountstaffserver.onrender.com";
  const { effectiveTimestamp } = useServerSyncedTime({ server });
  const currentUser = auth.currentUser || {};
  const showBirthday = FORCE_BIRTHDAY_GREETING || isBirthday(birthday, effectiveTimestamp);

  useEffect(() => {
    let active = true;
    setBirthday(null);
    loadBirthday({ email: currentUser.email }, userRole)
      .then(value => { if (active) setBirthday(value); })
      .catch(() => { if (active) setBirthday(null); });
    return () => { active = false; };
  }, [currentUser.email, userRole]);

  useEffect(() => {
    let mounted = true;

    const loadGreetingName = async () => {
      const name = await resolveFirstNameFromUser(currentUser);

      if (mounted) {
        setFirstName(name);
      }
    };

    loadGreetingName();

    return () => {
      mounted = false;
    };
  }, [currentUser?.uid, currentUser?.email, currentUser?.role]);

  useEffect(() => {
    // console.log("[Dashboard Announcements] Starting announcement listener");

    const unsubscribe = subscribeAnnouncements(
      (data) => {
        // console.log("[Dashboard Announcements] Raw announcements:", data);
        setAnnouncements(data || []);
      },
      (error) => {
        // console.error("[Dashboard Announcements] Listener error:", error);
      },
    );

    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    if (!userEmail || !dbFinancialPromotions) {
      setFinancialPromotions([]);
      return () => {};
    }

    const safeEmail = String(userEmail || "")
      .trim()
      .toLowerCase();

    if (!safeEmail) {
      setFinancialPromotions([]);
      return () => {};
    }

    const submissionsQuery = query(
      collection(dbFinancialPromotions, "Approvals", safeEmail, "Submissions"),
      orderBy("createdAt", "desc"),
      limit(10),
    );

    const unsubscribe = onSnapshot(
      submissionsQuery,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ownerEmail: safeEmail,
          ...docSnap.data(),
        }));

        setFinancialPromotions(rows);
      },
      () => {
        setFinancialPromotions([]);
      },
    );

    return () => unsubscribe?.();
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail || !dbAML) {
      setAmlChecks([]);
      return () => {};
    }

    const safeEmail = String(userEmail || "")
      .trim()
      .toLowerCase();

    if (!safeEmail) {
      setAmlChecks([]);
      return () => {};
    }

    const viewAll = canViewAllChecks(userRole, userPermissions);
    const unsubscribers = [];
    let cancelled = false;

    const setupListeners = async () => {
      try {
        if (viewAll) {
          const cgQuery = query(
            collectionGroup(dbAML, "Checks"),
            orderBy("createdAt", "desc"),
            limit(10),
          );

          const unsub = onSnapshot(
            cgQuery,
            (snap) => {
              if (cancelled) return;
              setAmlChecks(
                snap.docs.map((d) => ({
                  id: d.id,
                  ownerEmail: d.ref?.parent?.parent?.id || safeEmail,
                  ...d.data(),
                })),
              );
            },
            () => {
              if (!cancelled) setAmlChecks([]);
            },
          );
          unsubscribers.push(unsub);
        } else {
          const related = await getRelatedEmails(safeEmail, userRole);
          if (cancelled) return;

          const extraMails = Array.isArray(adminAdviserMails)
            ? adminAdviserMails
                .map((e) =>
                  String(e || "")
                    .trim()
                    .toLowerCase(),
                )
                .filter(Boolean)
            : [];

          const emailsToQuery = [
            ...new Set([safeEmail, ...related, ...extraMails]),
          ];

          const docsByEmail = new Map();

          const mergeAndSet = () => {
            const all = [];
            docsByEmail.forEach((docs) => all.push(...docs));
            all.sort((a, b) => {
              const toMs = (v) => {
                if (!v) return 0;
                if (typeof v?.toDate === "function")
                  return v.toDate().getTime();
                if (typeof v?.seconds === "number") return v.seconds * 1000;
                const p = new Date(v).getTime();
                return Number.isNaN(p) ? 0 : p;
              };
              return toMs(b.createdAt) - toMs(a.createdAt);
            });
            setAmlChecks(all.slice(0, 10));
          };

          emailsToQuery.forEach((email) => {
            const emailQuery = query(
              collection(dbAML, "AML Checks", email, "Checks"),
              orderBy("createdAt", "desc"),
              limit(10),
            );

            const unsub = onSnapshot(
              emailQuery,
              (snap) => {
                if (cancelled) return;
                docsByEmail.set(
                  email,
                  snap.docs.map((d) => ({
                    id: d.id,
                    ownerEmail: email,
                    ...d.data(),
                  })),
                );
                mergeAndSet();
              },
              () => {
                if (cancelled) return;
                docsByEmail.set(email, []);
                mergeAndSet();
              },
            );
            unsubscribers.push(unsub);
          });
        }
      } catch {
        if (!cancelled) setAmlChecks([]);
      }
    };

    setupListeners();

    return () => {
      cancelled = true;
      unsubscribers.forEach((u) => u?.());
    };
  }, [userEmail, userRole, userPermissions, adminAdviserMails]);

  useEffect(() => {
    if (!amlChecks.length) {
      setAmlChecksEnriched([]);
      return;
    }

    let cancelled = false;

    (async () => {
      const baseRows = await Promise.all(
        amlChecks.map(async (check) => {
          const ownerEmail = check.ownerEmail || userEmail || "";
          const checkId = check.id;

          let firstName = "";
          let lastName = "";
          let dob = "";
          let fullAddress = "";

          try {
            const reqSnap = await getDocs(
              collection(
                dbAML,
                "AML Checks",
                ownerEmail,
                "Checks",
                checkId,
                "Request Data",
              ),
            );
            const req = reqSnap.docs[0]?.data() || {};
            const pick = (obj, ...paths) => {
              for (const p of paths) {
                const val = p.split(".").reduce((o, k) => o?.[k], obj);
                if (val !== undefined && val !== null && val !== "") return val;
              }
              return undefined;
            };
            firstName =
              pick(req, "personal.firstName", "personalDetails.firstName") ||
              "";
            lastName =
              pick(req, "personal.lastName", "personalDetails.lastName") || "";
            dob =
              pick(
                req,
                "personal.dob",
                "personalDetails.dob",
                "personal.dateOfBirth",
              ) || "";
            const addrParts = [
              pick(req, "address.buildingName") ||
                pick(req, "address.buildingNumber"),
              pick(req, "address.street"),
              pick(req, "address.town"),
              pick(req, "address.county"),
              pick(req, "address.postcode"),
            ];
            fullAddress = addrParts.filter(Boolean).join(", ");
          } catch {}

          let experianResponse = {};
          let reasonsByRef = {};

          try {
            const expSnap = await getDocs(
              collection(
                dbAML,
                "AML Checks",
                ownerEmail,
                "Checks",
                checkId,
                "Experian Response",
              ),
            );
            const expDoc = expSnap.docs[0]?.data() || {};
            experianResponse = expDoc;
            reasonsByRef = expDoc?.reasonsByRef || {};
          } catch {}

          return {
            ...check,
            name: `${firstName} ${lastName}`.trim() || check.name || "",
            dob: dob || check.dob || "",
            fullAddress: fullAddress || check.fullAddress || "",
            experianResponse,
            reasonsByRef,
          };
        }),
      );

      if (cancelled) return;

      // Fetch live Veriff decisions
      const API_BASE = process.env.REACT_APP_AML_SERVER;
      const idvIds = [];
      const docOnlyIds = [];

      for (const row of baseRows) {
        if (!row.sessionId) continue;
        const bucket = classifyBucket(row.checkType);
        if (bucket === "idv") idvIds.push(row.sessionId);
        else if (bucket === "doconly") docOnlyIds.push(row.sessionId);
      }

      const decisionsBySession = new Map();

      if (API_BASE && (idvIds.length || docOnlyIds.length)) {
        try {
          const encodeIds = (ids) => ids.map(encodeURIComponent).join(",");
          const makeJson = async (res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
          };

          const jobs = [];
          if (idvIds.length) {
            jobs.push(
              fetch(`${API_BASE}get-idv-decisions/?ids=${encodeIds(idvIds)}`)
                .then(makeJson)
                .catch(() => null),
            );
          }
          if (docOnlyIds.length) {
            jobs.push(
              fetch(
                `${API_BASE}get-docOnly-decisions/?ids=${encodeIds(docOnlyIds)}`,
              )
                .then(makeJson)
                .catch(() => null),
            );
          }

          const results = await Promise.all(jobs);
          if (!cancelled) {
            for (const result of results) {
              for (const r of result?.rows || []) {
                const key = r.sessionId || r.id;
                if (key) decisionsBySession.set(key, r);
              }
            }
          }
        } catch {}
      }

      if (cancelled) return;

      const enriched = baseRows.map((row) => {
        const key = row.sessionId || row.id;
        const liveRow = decisionsBySession.get(key);
        if (!liveRow) return row;
        return {
          ...row,
          decision: liveRow.decision || row.decision || null,
        };
      });

      if (!cancelled) setAmlChecksEnriched(enriched);
    })();

    return () => {
      cancelled = true;
    };
  }, [amlChecks, userEmail]);

  const { greeting, formattedDateTime } = getPortalGreetingDataFromTimestamp(
    effectiveTimestamp,
    firstName,
  );

  const accountCreatedAtMs = useMemo(() => {
    return getFirebaseAccountCreatedAtMs(currentUser);
  }, [currentUser]);

  const adviserStatus = useMemo(() => {
    return currentUser?.adviserStatus || currentUser?.status || [];
  }, [currentUser]);

  const adminStatus = useMemo(() => {
    return currentUser?.adminStatus || currentUser?.status || [];
  }, [currentUser]);

  const visibleAnnouncements = useMemo(() => {
    const filtered = getVisibleAnnouncementsForUser({
      announcements,
      userRole,
      userPermissions,
      adviserStatus,
      adminStatus,
      nowTimestamp: effectiveTimestamp,
    });

    // console.log("[Dashboard Announcements] Visible announcements:", {
    //   userEmail,
    //   userRole,
    //   userPermissions,
    //   adviserStatus,
    //   adminStatus,
    //   rawCount: announcements.length,
    //   visibleCount: filtered.length,
    //   filtered,
    // });

    return filtered;
  }, [
    announcements,
    userEmail,
    userRole,
    userPermissions,
    adviserStatus,
    adminStatus,
    effectiveTimestamp,
  ]);

  const userRelevantAnnouncements = useMemo(() => {
    return visibleAnnouncements.filter((announcement) => {
      const announcementCreatedOrPostedMs =
        getAnnouncementCreatedOrPostedMs(announcement);

      if (!accountCreatedAtMs) return false;
      if (!announcementCreatedOrPostedMs) return false;

      return announcementCreatedOrPostedMs >= accountCreatedAtMs;
    });
  }, [visibleAnnouncements, accountCreatedAtMs]);

  //   const acknowledgedIds = useMemo(() => {
  //     const safeEmail = String(userEmail || "")
  //       .trim()
  //       .toLowerCase();

  //     if (!safeEmail) return [];

  //    const ids = userRelevantAnnouncements
  //      .filter((announcement) => {
  //        const acknowledgedBy = Array.isArray(announcement.acknowledgedBy)
  //          ? announcement.acknowledgedBy
  //          : [];

  //        return acknowledgedBy.some(
  //          (item) =>
  //            String(item?.userEmail || "")
  //              .trim()
  //              .toLowerCase() === safeEmail,
  //        );
  //      })
  //      .map((announcement) => announcement.id);

  //     // console.log("[Dashboard Announcements] Acknowledged announcement IDs:", {
  //     //   safeEmail,
  //     //   ids,
  //     // });

  //     return ids;
  //   }, [userRelevantAnnouncements, userEmail]);

  useEffect(() => {
    if (!userEmail) {
      setAcknowledgedIds([]);
      return;
    }

    const unsubscribe = subscribeUserAnnouncementAcknowledgements(
      userEmail,
      (ids) => {
        setAcknowledgedIds(ids || []);
      },
      (error) => {
        console.error(
          "[Dashboard Announcements] Acknowledgement listener error:",
          error,
        );
      },
    );

    return () => unsubscribe?.();
  }, [userEmail]);
  const latestAnnouncements = useMemo(() => {
    return userRelevantAnnouncements.slice(0, 1);
  }, [userRelevantAnnouncements]);

  const pastAnnouncements = useMemo(() => {
    return userRelevantAnnouncements.slice(1, 7);
  }, [userRelevantAnnouncements]);

  const popupAnnouncements = useMemo(() => {
    return userRelevantAnnouncements.filter((announcement) => {
      const hasNotAcknowledged = !acknowledgedIds.includes(announcement.id);

      return announcement.notifyOnLogin === true && hasNotAcknowledged;
    });
  }, [userRelevantAnnouncements, acknowledgedIds]);

  const handleOpenFinancialPromotion = (submission) => {
    if (!submission?.id) return;

    navigate(`/financial-promotions/details/${submission.id}`, {
      state: {
        submissionId: submission.id,
        ownerEmail:
          submission?.ownerEmail ||
          submission?.createdByEmail ||
          userEmail ||
          "",
      },
    });
  };

  const handleOpenAMLCheck = (check) => {
    if (!check?.id) return;

    const fullName = getAMLClientName(check);
    const verifiedDob = check?.decision?.verification?.person?.dob;
    const dob = check?.dob || verifiedDob || "";

    navigate("/check²/result", {
      state: {
        sessionId: check?.sessionId || "",
        vendorData:
          check?.decision?.verification?.vendorData ||
          check?.vendorData ||
          check?.id ||
          "",
        forAdviser: check?.forAdviser || "",
        checkType: check?.checkType || "",
        createdBy: check?.createdBy || "",
        createdAt: check?.createdAt || "",
        name: fullName,
        dob,
        fullAddress: check?.fullAddress || "",
        sessionUrl: check?.sessionUrl || "",
        expiration: formatAmlExpiration(check?.createdAt),
        experianResponse: check?.experianResponse || {},
        reasonsByRef: check?.reasonsByRef || {},
      },
    });
  };
  const notifications = useMemo(() => {
    const announcementNotifications = userRelevantAnnouncements.map(
      (announcement) => {
        const acknowledged = acknowledgedIds.includes(announcement.id);

        return {
          id: `announcement-${announcement.id}`,
          typeLabel: announcement.contentType || "Announcement",
          title: announcement.contentTitle || "Announcement",
          timestamp: getAnnouncementTimestamp(announcement),
          statusLabel: acknowledged ? "Acknowledged" : "Active",
          statusClassName: acknowledged
            ? "dashboard-notification-pill--acknowledged"
            : "dashboard-notification-pill--active",
          onClick: null,
        };
      },
    );

    const financialPromotionNotifications = financialPromotions.map(
      (submission) => {
        const statusMeta = getFinancialPromotionStatusMeta(submission?.status);
        const timestamp =
          getTimestampMs(submission?.updatedAt) ||
          getTimestampMs(submission?.createdAt);

        return {
          id: `fp-${submission.id}`,
          typeLabel: "Financial Promotions",
          title:
            submission?.title ||
            submission?.contentType ||
            "Financial Promotion",
          timestamp,
          statusLabel: statusMeta.label,
          statusClassName: statusMeta.className,
          onClick: () => handleOpenFinancialPromotion(submission),
        };
      },
    );

    const amlNotifications = amlChecksEnriched.map((check) => {
      const { checkResult } = computeOverallOutcome({
        rowOrCheck: check,
        checkType: check.checkType,
        experianResponse: check.experianResponse,
        reasonsByRef: check.reasonsByRef,
        getAllMatchesResolved,
      });

      const resultLabel = checkResult || "Awaiting Response";
      const statusColor = colorForResult(resultLabel);

      return {
        id: `aml-${check.id}`,
        typeLabel: "check\u00b2",
        title: getAMLClientName(check),
        subtitle: formatCheckType(check?.checkType) || "AML Check",
        timestamp: getTimestampMs(check?.createdAt),
        statusLabel: capitalizeFirst(resultLabel),
        statusColor,
        statusClassName: amlColorToCardClass(statusColor),
        onClick: () => handleOpenAMLCheck(check),
      };
    });
    return [
      // ...announcementNotifications,
      ...financialPromotionNotifications,
      ...amlNotifications,
    ]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 1000);
  }, [
    userRelevantAnnouncements,
    acknowledgedIds,
    financialPromotions,
    amlChecksEnriched,
  ]);

  return (
    <div className="Main-Page">
      <Helmet>
        <title>My Dashboard</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container GhostWhite">
        <div className="portal-section-page GhostWhite">
          <div className="portal-section-hero">
            {showBirthday && <div className="dashboard-birthday-decorations" aria-hidden="true">
              {Array.from({ length: 24 }, (_, index) => (
                <span key={index} className="dashboard-banner-confetti" style={{ "--confetti-index": index }} />
              ))}
              <span className="dashboard-birthday-balloon balloon-one" />
              <span className="dashboard-birthday-balloon balloon-two" />
              <span className="dashboard-birthday-balloon balloon-three" />
              <span className="dashboard-birthday-balloon balloon-four" />
            </div>}
            <div className="portal-section-bubbles" hidden={showBirthday}>
              <span className="portal-section-bubble portal-bubble-1" />
              <span className="portal-section-bubble portal-bubble-2" />
              <span className="portal-section-bubble portal-bubble-3" />
              <span className="portal-section-bubble portal-bubble-4" />
              <span className="portal-section-bubble portal-bubble-5" />
              <span className="portal-section-bubble portal-bubble-6" />
              <span className="portal-section-bubble portal-bubble-7" />
            </div>

            <div className="portal-section-header">
              <div className="portal-section-hero-layout">
                <div className="portal-section-greeting-wrap">
                  <div className="dashboard-greeting-copy fit-column gap-xs">
                    <h3 className="FP_H3">{showBirthday ? `Happy Birthday${firstName ? `, ${firstName}` : ""}!` : greeting}</h3>
                    {showBirthday && <span className="FP_Help_Text_Semibold primary">Wishing you a wonderful day, with warm wishes from all of us at Rosemount!</span>}
                  </div>
                </div>

                <div className="portal-section-info-stack">
                  <div className="portal-section-title-chip">
                    <div className="dashboard-announcements-main-header">
                      <CalenderIcon className="dashboard-announcements-main-icon" />
                      <div className="portal-section-date-value FP_Help_Text_Semibold">
                        {formattedDateTime}
                      </div>
                    </div>
                  </div>

                  {userRole === "Adviser" && (
                    <div
                      className="portal-section-title-chip"
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate("/my-profile")}
                    >
                      <div className="dashboard-announcements-main-header">
                        <MyProfile className="my-profile-icon" />
                        <div className="portal-section-date-value FP_Help_Text_Semibold">
                          My Profile
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <section className="dashboard-announcements-layout">
            <RatesMonitor server={server} />
            <div className="dashboard-announcements-main">
              <div className="dashboard-announcements-main-header">
                <LatestAnnouncetmentIcon className="dashboard-announcements-main-icon" />
                <span className="FP_Body_Semibold primary">
                  Latest Announcement
                </span>
              </div>

              {latestAnnouncements.length === 0 ? (
                <div className="dashboard-announcements-empty FP_Content">
                  No announcements available.
                </div>
              ) : (
                latestAnnouncements.map((announcement) => (
                  <AnnouncementCard
                    key={announcement.id}
                    announcement={announcement}
                    variant="latest"
                    userRole={userRole}
                    userName={currentUser?.displayName || ""}
                  />
                ))
              )}
            </div>

            <div className="dashboard-announcements-side">
              <div className="dashboard-announcements-main-header">
                <Notifications className="dashboard-announcements-main-icon" />
                <span className="FP_Body_Semibold primary">Notifications</span>
              </div>

              <div className="dashboard-notification-tabs FP_Help_Text_Semibold">
              <Tabs
                tabs={["All", "Financial Promotions", "check²", ...(canSeeLoaTracker ? ["LOA Tracker"] : [])].map((filter) => ({ id: filter, label: filter }))}
                activeTab={notificationFilter}
                onTabChange={setNotificationFilter}
              />
              </div>
              {notifications.filter((item) => notificationFilter === "All" || item.typeLabel === notificationFilter).length === 0 && (
                <div className="dashboard-announcements-empty FP_Content">No notifications available.</div>
              )}
              {notifications.filter((item) => notificationFilter === "All" || item.typeLabel === notificationFilter).map((notification) => {
                const cardClassName = `dashboard-notification-card ${
                  notification.onClick
                    ? "dashboard-notification-card--clickable"
                    : ""
                } ${notification.statusClassName || ""}`;

                const formattedDate = notification.timestamp
                  ? dayjs(notification.timestamp).format("DD/MM/YYYY")
                  : "";

                const CardTag = notification.onClick ? "button" : "div";

                return (
                  <CardTag
                    key={notification.id}
                    type={notification.onClick ? "button" : undefined}
                    className={cardClassName}
                    onClick={notification.onClick || undefined}
                  >
                    <div className="dashboard-notification-meta-row">
                      <div className="FP_Help_Text_Semibold">
                        {notification.typeLabel}
                      </div>

                      <div className="FP_Help_Text grey">{formattedDate}</div>
                    </div>

                    <div className="dashboard-notification-text FP_Help_Text">
                      {notification.title}
                    </div>

                    {/* {notification.subtitle && (
                      <div className="FP_Help_Text grey">
                        {notification.subtitle}
                      </div>
                    )} */}

                    <div
                      className="dashboard-notification-status"
                      style={{
                        justifyContent: notification.statusColor
                          ? "space-between"
                          : "flex-end",
                      }}
                    >
                      <div className="FP_Help_Text grey">
                        {notification.subtitle
                          ? `${notification.subtitle} • `
                          : ""}
                        {notification.statusColor ? "Result" : ""}
                      </div>
                      {/* {notification.statusColor ? (
                        <StatusButton
                          type={notification.statusLabel}
                          color={notification.statusColor}
                        />
                      ) : (
                        <StatusTag status={notification.statusLabel} />
                      )} */}
                      <StatusTag status={notification.statusLabel} />
                    </div>
                  </CardTag>
                );
              })}
            </div>

            <div className="dashboard-past-announcements">
              <div className="dashboard-announcements-main-header">
                <PastAnnouncementIcon className="dashboard-announcements-main-icon" />
                <span className="FP_Body_Semibold primary">
                  Past Announcements
                </span>
              </div>

              <div className="dashboard-past-announcements-grid">
                {pastAnnouncements.length === 0 ? (
                  <div className="dashboard-announcements-empty FP_Content">
                    No past announcements available.
                  </div>
                ) : (
                  pastAnnouncements.map((announcement) => (
                    <AnnouncementCard
                      key={announcement.id}
                      announcement={announcement}
                      variant="past"
                      userRole={userRole}
                      userName={currentUser?.displayName || ""}
                    />
                  ))
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      <AnnouncementPopup
        announcements={popupAnnouncements}
        acknowledgedIds={acknowledgedIds}
        userEmail={userEmail}
        userRole={userRole}
        userName={currentUser?.displayName || ""}
      />
    </div>
  );
};

export default MyDashboard;
